import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '../services/supabase'
import { useOrganizationId } from '../hooks/useOrganizationId'
import { useOrganization } from '../contexts/OrganizationContext'
import Card from './Card'
import Button from './Button'
import Input from './Input'
import Textarea from './Textarea'
import {
  UtensilsCrossed,
  Plus,
  X,
  AlertTriangle,
  CheckCircle2,
  Search,
  Pill,
  Leaf,
  FileText,
} from 'lucide-react'
import {
  getNutritionTherapy,
  saveNutritionTherapy,
  checkDrugHerbInteractions,
  getClientHealthData,
} from '../services/nutritionTherapy'
import TaggedHabitsView from './TaggedHabitsView'

const NutritionTherapy = ({ carePlanId, clientId }) => {
  const [therapy, setTherapy] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [clientHealthData, setClientHealthData] = useState({})
  const [interactions, setInteractions] = useState([])
  const [showInteractionModal, setShowInteractionModal] = useState(false)
  const organizationId = useOrganizationId()
  const { canWrite } = useOrganization()

  // Form state
  const [effectsToAvoid, setEffectsToAvoid] = useState([])
  const [desiredFoodEffects, setDesiredFoodEffects] = useState([])
  const [desiredHerbEffects, setDesiredHerbEffects] = useState([])
  const [generalNotes, setGeneralNotes] = useState('')
  const [isEditing, setIsEditing] = useState(false)

  useEffect(() => {
    if (carePlanId && organizationId) {
      fetchNutritionTherapy()
      if (clientId) {
        fetchClientHealthData()
      }
    }
  }, [carePlanId, organizationId, clientId])

  const fetchNutritionTherapy = async () => {
    if (!carePlanId || !organizationId) return
    try {
      setLoading(true)
      const data = await getNutritionTherapy(carePlanId, organizationId)
      if (data) {
        setTherapy(data)
        // Ensure foods and herbs are arrays
        setEffectsToAvoid((data.effects_to_avoid || []).map(effect => ({
          ...effect,
          foods: Array.isArray(effect.foods) ? effect.foods : (effect.foods ? [effect.foods] : [])
        })))
        setDesiredFoodEffects((data.desired_food_effects || []).map(effect => ({
          ...effect,
          foods: Array.isArray(effect.foods) ? effect.foods : (effect.foods ? [effect.foods] : [])
        })))
        setDesiredHerbEffects((data.desired_herb_effects || []).map(effect => ({
          ...effect,
          herbs: Array.isArray(effect.herbs) ? effect.herbs : (effect.herbs ? [effect.herbs] : [])
        })))
        setGeneralNotes(data.general_notes || '')
        setInteractions(data.drug_herb_interactions || [])
        setIsEditing(false) // Set to read-only mode when data is loaded
      } else {
        setIsEditing(true) // Allow editing if no data exists
      }
    } catch (error) {
      console.error('Error fetching nutrition therapy:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchClientHealthData = async () => {
    if (!clientId || !organizationId) return
    try {
      const data = await getClientHealthData(clientId, organizationId)
      setClientHealthData(data)
    } catch (error) {
      console.error('Error fetching client health data:', error)
    }
  }

  const checkInteractions = async () => {
    if (!clientId || !organizationId) return

    try {
      // Extract all herbs from desired herb effects
      const allHerbs = desiredHerbEffects
        .flatMap((effect) => effect.herbs || [])
        .filter((h) => h.trim())

      if (allHerbs.length === 0) {
        setInteractions([])
        return
      }

      const interactionData = await checkDrugHerbInteractions(
        clientId,
        organizationId,
        allHerbs
      )

      setInteractions(interactionData)
      if (interactionData.length > 0) {
        setShowInteractionModal(true)
      }
    } catch (error) {
      console.error('Error checking interactions:', error)
    }
  }

  useEffect(() => {
    // Auto-check interactions when herbs change
    if (desiredHerbEffects.length > 0 && clientId) {
      const timer = setTimeout(() => {
        checkInteractions()
      }, 1000) // Debounce

      return () => clearTimeout(timer)
    }
  }, [desiredHerbEffects, clientId])

  const addEffectToAvoid = () => {
    setEffectsToAvoid([
      ...effectsToAvoid,
      {
        effect: '',
        foods: [],
        notes: '',
        preparation: '',
      },
    ])
  }

  const updateEffectToAvoid = (index, field, value) => {
    const updated = [...effectsToAvoid]
    updated[index] = { ...updated[index], [field]: value }
    setEffectsToAvoid(updated)
  }

  const removeEffectToAvoid = (index) => {
    setEffectsToAvoid(effectsToAvoid.filter((_, i) => i !== index))
  }

  const addFoodEffect = () => {
    setDesiredFoodEffects([
      ...desiredFoodEffects,
      {
        effect: '',
        foods: [],
        notes: '',
        preparation: '',
        recipe_reference: '',
      },
    ])
  }

  const addFoodToEffect = (effectIndex) => {
    const updated = [...desiredFoodEffects]
    if (!updated[effectIndex].foods) {
      updated[effectIndex].foods = []
    }
    updated[effectIndex].foods.push('')
    setDesiredFoodEffects(updated)
  }

  const removeFoodFromEffect = (effectIndex, foodIndex) => {
    const updated = [...desiredFoodEffects]
    updated[effectIndex].foods = updated[effectIndex].foods.filter((_, i) => i !== foodIndex)
    setDesiredFoodEffects(updated)
  }

  const updateFoodInEffect = (effectIndex, foodIndex, value) => {
    const updated = [...desiredFoodEffects]
    updated[effectIndex].foods[foodIndex] = value
    setDesiredFoodEffects(updated)
  }

  const updateFoodEffect = (index, field, value) => {
    const updated = [...desiredFoodEffects]
    updated[index] = { ...updated[index], [field]: value }
    setDesiredFoodEffects(updated)
  }

  const removeFoodEffect = (index) => {
    setDesiredFoodEffects(desiredFoodEffects.filter((_, i) => i !== index))
  }

  const addHerbEffect = () => {
    setDesiredHerbEffects([
      ...desiredHerbEffects,
      {
        effect: '',
        herbs: [],
        notes: '',
        preparation: '',
        recipe_reference: '',
        dosage: '',
      },
    ])
  }

  const addHerbToEffect = (effectIndex) => {
    const updated = [...desiredHerbEffects]
    if (!updated[effectIndex].herbs) {
      updated[effectIndex].herbs = []
    }
    updated[effectIndex].herbs.push('')
    setDesiredHerbEffects(updated)
  }

  const removeHerbFromEffect = (effectIndex, herbIndex) => {
    const updated = [...desiredHerbEffects]
    updated[effectIndex].herbs = updated[effectIndex].herbs.filter((_, i) => i !== herbIndex)
    setDesiredHerbEffects(updated)
  }

  const updateHerbInEffect = (effectIndex, herbIndex, value) => {
    const updated = [...desiredHerbEffects]
    updated[effectIndex].herbs[herbIndex] = value
    setDesiredHerbEffects(updated)
  }

  const updateHerbEffect = (index, field, value) => {
    const updated = [...desiredHerbEffects]
    updated[index] = { ...updated[index], [field]: value }
    setDesiredHerbEffects(updated)
    
    // If herbs changed, check interactions
    if (field === 'herbs' && clientId) {
      setTimeout(() => checkInteractions(), 500)
    }
  }

  const removeHerbEffect = (index) => {
    setDesiredHerbEffects(desiredHerbEffects.filter((_, i) => i !== index))
  }

  const handleSave = async () => {
    if (!carePlanId || !clientId || !organizationId || !canWrite('care_plans')) return

    try {
      setSaving(true)
      
      // Update interactions based on current herbs
      const updatedInteractions = interactions

      await saveNutritionTherapy({
        organizationId,
        carePlanId,
        clientId,
        effectsToAvoid,
        desiredFoodEffects,
        desiredHerbEffects,
        drugHerbInteractions: updatedInteractions,
        generalNotes,
      })

      await fetchNutritionTherapy()
      setIsEditing(false) // Switch to read-only mode after saving
      alert('Nutrition therapy saved successfully')
    } catch (error) {
      console.error('Error saving nutrition therapy:', error)
      alert(error.message || 'Failed to save nutrition therapy')
    } finally {
      setSaving(false)
    }
  }

  const parseList = (text) => {
    if (!text) return []
    return text
      .split(/[,\n]/)
      .map((item) => item.trim())
      .filter((item) => item.length > 0)
  }

  const formatList = (items) => {
    if (!items) return ''
    if (Array.isArray(items)) {
      return items.filter(item => item && item.trim()).join(', ')
    }
    return String(items)
  }

  if (loading) {
    return <div className="text-sm text-gray-500">Loading nutrition therapy...</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <UtensilsCrossed size={20} />
          Nutrition Therapy
        </h3>
        <div className="flex items-center gap-2">
          {canWrite('care_plans') && therapy && !isEditing && (
            <Button variant="secondary" onClick={() => setIsEditing(true)}>
              Edit
            </Button>
          )}
          {canWrite('care_plans') && isEditing && (
            <>
              <Button variant="secondary" onClick={() => {
                setIsEditing(false)
                fetchNutritionTherapy() // Reload original data
              }}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Tagged Habits View */}
      {clientId && organizationId && (
        <TaggedHabitsView clientId={clientId} organizationId={organizationId} />
      )}

      {/* Client Health Data Summary */}
      {clientId && (clientHealthData.allergies || clientHealthData.medications) && (
        <Card variant="soft" className="p-4 bg-blue-50 border border-blue-200">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-blue-900 mb-2">Client Health Information</h4>
              {clientHealthData.allergies && (
                <div className="mb-2">
                  <p className="text-xs font-semibold text-blue-700 uppercase">Allergies:</p>
                  <p className="text-sm text-blue-800">{clientHealthData.allergies}</p>
                </div>
              )}
              {clientHealthData.medications && (
                <div>
                  <p className="text-xs font-semibold text-blue-700 uppercase">Current Medications:</p>
                  <p className="text-sm text-blue-800">{clientHealthData.medications}</p>
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Drug-Herb Interactions Warning */}
      {interactions.length > 0 && (
        <Card variant="soft" className="p-4 bg-red-50 border-2 border-red-200">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-red-900 mb-2">
                Drug-Herb Interactions Detected ({interactions.length})
              </h4>
              <div className="space-y-2">
                {interactions.slice(0, 3).map((interaction, idx) => (
                  <div key={idx} className="text-sm">
                    <p className="font-semibold text-red-800">
                      {interaction.medication_name} + {interaction.herb_name}
                    </p>
                    <p className="text-red-700">
                      <span className="uppercase font-semibold">{interaction.severity}</span> -{' '}
                      {interaction.description}
                    </p>
                    {interaction.recommendations && (
                      <p className="text-red-600 text-xs mt-1">
                        Recommendation: {interaction.recommendations}
                      </p>
                    )}
                  </div>
                ))}
              </div>
              {interactions.length > 3 && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowInteractionModal(true)}
                  className="mt-2"
                >
                  View All Interactions ({interactions.length})
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Therapeutic Effects to Avoid */}
      <Card variant="soft" className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <AlertTriangle size={18} className="text-red-600" />
            Therapeutic Effects to Avoid
          </h4>
          {canWrite('care_plans') && isEditing && (
            <Button variant="secondary" size="sm" onClick={addEffectToAvoid}>
              <Plus size={16} className="mr-1" />
              Add Effect
            </Button>
          )}
        </div>
        {effectsToAvoid.length === 0 ? (
          <p className="text-sm text-gray-500">No effects to avoid specified</p>
        ) : (
          <div className="space-y-4">
            {effectsToAvoid.map((effect, index) => (
              <Card key={index} variant="soft" className="p-4 border border-red-100">
                <div className="flex items-start justify-between mb-3">
                  <Input
                    placeholder="Effect to avoid (e.g., Inflammation, Blood sugar spike)"
                    value={effect.effect}
                    onChange={(e) => updateEffectToAvoid(index, 'effect', e.target.value)}
                    disabled={!canWrite('care_plans') || !isEditing}
                    className="flex-1 mr-2"
                  />
                  {canWrite('care_plans') && isEditing && (
                    <button
                      onClick={() => removeEffectToAvoid(index)}
                      className="text-red-500 hover:text-red-700 p-1"
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Textarea
                    label="Foods to Avoid"
                    placeholder="e.g., Processed sugars, Red meat, Dairy..."
                    value={formatList(effect.foods)}
                    onChange={(e) =>
                      updateEffectToAvoid(index, 'foods', parseList(e.target.value))
                    }
                    disabled={!canWrite('care_plans') || !isEditing}
                    rows={2}
                  />
                  <Textarea
                    label="Notes"
                    placeholder="Additional information..."
                    value={effect.notes}
                    onChange={(e) => updateEffectToAvoid(index, 'notes', e.target.value)}
                    disabled={!canWrite('care_plans') || !isEditing}
                    rows={2}
                  />
                  <Input
                    label="Preparation Protocol"
                    placeholder="e.g., Avoid raw, cook thoroughly"
                    value={effect.preparation}
                    onChange={(e) => updateEffectToAvoid(index, 'preparation', e.target.value)}
                    disabled={!canWrite('care_plans') || !isEditing}
                  />
                </div>
              </Card>
            ))}
          </div>
        )}
      </Card>

      {/* Desired Food Effects */}
      <Card variant="soft" className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <UtensilsCrossed size={18} className="text-green-600" />
            Desired Therapeutic Effects (Foods)
          </h4>
          {canWrite('care_plans') && isEditing && (
            <Button variant="secondary" size="sm" onClick={addFoodEffect}>
              <Plus size={16} className="mr-1" />
              Add Effect
            </Button>
          )}
        </div>
        {desiredFoodEffects.length === 0 ? (
          <p className="text-sm text-gray-500">No desired food effects specified</p>
        ) : (
          <div className="space-y-4">
            {desiredFoodEffects.map((effect, index) => (
              <Card key={index} variant="soft" className="p-4 border border-green-100">
                <div className="flex items-start justify-between mb-3">
                  <Input
                    placeholder="Desired effect (e.g., Anti-inflammatory, Digestive support)"
                    value={effect.effect}
                    onChange={(e) => updateFoodEffect(index, 'effect', e.target.value)}
                    disabled={!canWrite('care_plans') || !isEditing}
                    className="flex-1 mr-2"
                  />
                  {canWrite('care_plans') && isEditing && (
                    <button
                      onClick={() => removeFoodEffect(index)}
                      className="text-red-500 hover:text-red-700 p-1"
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Foods to Include
                    </label>
                    {isEditing ? (
                      <div className="space-y-2">
                        {(effect.foods || []).map((food, foodIndex) => (
                          <div key={foodIndex} className="flex items-center gap-2">
                            <Input
                              placeholder="e.g., Turmeric, Ginger, Leafy greens..."
                              value={food}
                              onChange={(e) => updateFoodInEffect(index, foodIndex, e.target.value)}
                              disabled={!canWrite('care_plans') || !isEditing}
                              className="flex-1"
                            />
                            <button
                              type="button"
                              onClick={() => removeFoodFromEffect(index, foodIndex)}
                              className="text-red-500 hover:text-red-700 p-1"
                              disabled={!canWrite('care_plans') || !isEditing}
                            >
                              <X size={18} />
                            </button>
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => addFoodToEffect(index)}
                          disabled={!canWrite('care_plans') || !isEditing}
                          className="flex items-center gap-1"
                        >
                          <Plus size={14} />
                          Add Food
                        </Button>
                      </div>
                    ) : (
                      <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <p className="text-sm text-gray-900 dark:text-white">
                          {formatList(effect.foods) || 'No foods specified'}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Textarea
                      label="Notes"
                      placeholder="Additional information, timing, frequency..."
                      value={effect.notes}
                      onChange={(e) => updateFoodEffect(index, 'notes', e.target.value)}
                      disabled={!canWrite('care_plans') || !isEditing}
                      rows={2}
                    />
                    <Input
                      label="Best Preparation Protocol"
                      placeholder="e.g., Steam lightly, Raw, Fermented"
                      value={effect.preparation}
                      onChange={(e) => updateFoodEffect(index, 'preparation', e.target.value)}
                      disabled={!canWrite('care_plans') || !isEditing}
                    />
                    <Input
                      label="Recipe Reference"
                      placeholder="e.g., Recipe book, Online link, Page number"
                      value={effect.recipe_reference}
                      onChange={(e) => updateFoodEffect(index, 'recipe_reference', e.target.value)}
                      disabled={!canWrite('care_plans') || !isEditing}
                      className="md:col-span-2"
                    />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </Card>

      {/* Desired Herb Effects */}
      <Card variant="soft" className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Leaf size={18} className="text-green-600" />
            Desired Therapeutic Effects (Herbs)
          </h4>
          {canWrite('care_plans') && isEditing && (
            <Button variant="secondary" size="sm" onClick={addHerbEffect}>
              <Plus size={16} className="mr-1" />
              Add Effect
            </Button>
          )}
        </div>
        {desiredHerbEffects.length === 0 ? (
          <p className="text-sm text-gray-500">No desired herb effects specified</p>
        ) : (
          <div className="space-y-4">
            {desiredHerbEffects.map((effect, index) => (
              <Card key={index} variant="soft" className="p-4 border border-green-100">
                <div className="flex items-start justify-between mb-3">
                  <Input
                    placeholder="Desired effect (e.g., Immune support, Stress reduction)"
                    value={effect.effect}
                    onChange={(e) => updateHerbEffect(index, 'effect', e.target.value)}
                    disabled={!canWrite('care_plans') || !isEditing}
                    className="flex-1 mr-2"
                  />
                  {canWrite('care_plans') && isEditing && (
                    <button
                      onClick={() => removeHerbEffect(index)}
                      className="text-red-500 hover:text-red-700 p-1"
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Herbs to Use
                    </label>
                    {isEditing ? (
                      <div className="space-y-2">
                        {(effect.herbs || []).map((herb, herbIndex) => (
                          <div key={herbIndex} className="flex items-center gap-2">
                            <Input
                              placeholder="e.g., Echinacea, Ashwagandha, Turmeric..."
                              value={herb}
                              onChange={(e) => updateHerbInEffect(index, herbIndex, e.target.value)}
                              disabled={!canWrite('care_plans') || !isEditing}
                              className="flex-1"
                            />
                            <button
                              type="button"
                              onClick={() => removeHerbFromEffect(index, herbIndex)}
                              className="text-red-500 hover:text-red-700 p-1"
                              disabled={!canWrite('care_plans') || !isEditing}
                            >
                              <X size={18} />
                            </button>
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => addHerbToEffect(index)}
                          disabled={!canWrite('care_plans') || !isEditing}
                          className="flex items-center gap-1"
                        >
                          <Plus size={14} />
                          Add Herb
                        </Button>
                      </div>
                    ) : (
                      <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <p className="text-sm text-gray-900 dark:text-white">
                          {formatList(effect.herbs) || 'No herbs specified'}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Textarea
                      label="Notes"
                      placeholder="Additional information, timing, contraindications..."
                      value={effect.notes}
                      onChange={(e) => updateHerbEffect(index, 'notes', e.target.value)}
                      disabled={!canWrite('care_plans') || !isEditing}
                      rows={2}
                    />
                    <Input
                      label="Dosage"
                      placeholder="e.g., 500mg twice daily, 1 tsp in tea"
                      value={effect.dosage}
                      onChange={(e) => updateHerbEffect(index, 'dosage', e.target.value)}
                      disabled={!canWrite('care_plans') || !isEditing}
                    />
                    <Input
                      label="Preparation Protocol"
                      placeholder="e.g., Tea, Tincture, Capsule, Topical"
                      value={effect.preparation}
                      onChange={(e) => updateHerbEffect(index, 'preparation', e.target.value)}
                      disabled={!canWrite('care_plans') || !isEditing}
                    />
                    <Input
                      label="Recipe Reference"
                      placeholder="e.g., Herbal reference book, Page number"
                      value={effect.recipe_reference}
                      onChange={(e) => updateHerbEffect(index, 'recipe_reference', e.target.value)}
                      disabled={!canWrite('care_plans') || !isEditing}
                      className="md:col-span-2"
                    />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </Card>

      {/* General Notes */}
      <Card variant="soft" className="p-6">
        <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <FileText size={18} />
          General Notes
        </h4>
        <Textarea
          placeholder="Additional notes about the nutrition therapy plan..."
          value={generalNotes}
          onChange={(e) => setGeneralNotes(e.target.value)}
          disabled={!canWrite('care_plans') || !isEditing}
          rows={4}
        />
      </Card>

      {/* Interactions Modal */}
      <AnimatePresence>
        {showInteractionModal && interactions.length > 0 && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowInteractionModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <Card variant="glass" className="w-full max-w-2xl max-h-[80vh] overflow-y-auto p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <AlertTriangle size={24} className="text-red-600" />
                    Drug-Herb Interactions
                  </h3>
                  <button
                    onClick={() => setShowInteractionModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X size={20} />
                  </button>
                </div>
                <div className="space-y-3">
                  {interactions.map((interaction, idx) => (
                    <Card
                      key={idx}
                      variant="soft"
                      className={`p-4 border-2 ${
                        interaction.severity === 'severe'
                          ? 'border-red-300 bg-red-50'
                          : interaction.severity === 'moderate'
                          ? 'border-yellow-300 bg-yellow-50'
                          : 'border-blue-300 bg-blue-50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <Pill size={20} className="flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-semibold text-gray-900">
                              {interaction.medication_name} + {interaction.herb_name}
                            </h4>
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                interaction.severity === 'severe'
                                  ? 'bg-red-100 text-red-700'
                                  : interaction.severity === 'moderate'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-blue-100 text-blue-700'
                              }`}
                            >
                              {interaction.severity.toUpperCase()}
                            </span>
                            <span className="px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                              {interaction.interaction_type}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 mb-2">{interaction.description}</p>
                          {interaction.mechanism && (
                            <p className="text-xs text-gray-600 mb-2">
                              <strong>Mechanism:</strong> {interaction.mechanism}
                            </p>
                          )}
                          {interaction.recommendations && (
                            <p className="text-sm font-semibold text-gray-800">
                              <strong>Recommendation:</strong> {interaction.recommendations}
                            </p>
                          )}
                          {interaction.source && (
                            <p className="text-xs text-gray-500 mt-2">Source: {interaction.source}</p>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </Card>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}

export default NutritionTherapy

